<template>
  <div class="offcanvas offcanvas-end" tabindex="-1" :id="offcanvas_id" data-bs-backdrop="static" :aria-labelledby="`${offcanvas_id}_label`">
    <div class="offcanvas-header">
      <button class="btn-back" type="button" name="button" data-test="btnCloseOffcanvas" data-bs-dismiss="offcanvas" aria-label="Close" @click="doCancel">
        <IconArrowBack/>
        {{ $t('goBack') }}
      </button>
      <h5 class="offcanvas-title d-block" :id="`${offcanvas_id}_label`">{{ title }}</h5>
    </div>
    <div class="offcanvas-body">
      <vee-form v-slot="{ handleSubmit }" as="div" ref="changePasswordForm">
        <form @submit.prevent.stop="handleSubmit(changePassword)">
            <div class="form-floating">
              <vee-field name="currentPassword" v-model="currentPassword" rules="required" v-slot="{ field, errorMessage }">
                <input v-bind="field" :type="showCurrentPassword ? 'text' : 'password'"
                        class="form-control" ref="currentPassword" id="currentPassword" :placeholder="$t('user.currentPassword')" data-test="inputCurrentPassword"/>

                <button class="btn-icon" type="button" name="button" data-test="btnShowNewPassword" @click="showCurrentPassword = !showCurrentPassword">
                  <IconEyePassword v-if="showCurrentPassword" />
                  <IconShowPassword v-else />
                </button>
                <span class="vee-error">{{ errorMessage }}</span>
              </vee-field>
              <label for="currentPassword">{{ $t('user.currentPassword') }}</label>
            </div>

              <div class="form-floating">
                <vee-field name="newPassword" v-model="newPassword" rules="required|min:8" v-slot="{ field, errorMessage }">
                  <input v-bind="field" :type="showNewPassword ? 'text' : 'password'"
                        class="form-control" ref="newPassword" id="newPassword"
                        @keyup="checkPassword(newPassword)" data-test="inputNewPassword" :placeholder="$t('user.newPassword')"/>

                  <button class="btn-icon" type="button" name="button" data-test="btnShowNewPassword" @click="showNewPassword = !showNewPassword">
                    <IconEyePassword v-if="showNewPassword" />
                    <IconShowPassword v-else />
                  </button>
                  <small class="strength-password" :class="strengthInfo" v-show="strengthInfo">{{ strengthInfo ? $t(`strengthPassword.${strengthInfo}`) : "" }}</small>
                  <span class="vee-error">{{ errorMessage ? errorMessage : $t(`${strengthPasswordMessage}`) }}</span>
                </vee-field>
                <label for="newPassword">{{ $t('user.newPassword') }}</label>
              </div>

            <div class="form-floating">
              <vee-field name="confirmNewPassword" v-model="newPasswordConfirmation" rules="required|confirmed:newPassword" v-slot="{ field, errorMessage }">
                <input v-bind="field" :type="showConfirmNewPassword ? 'text' : 'password'"
                         class="form-control" ref="passwordConfirmation" id="passwordConfirmation" data-test="inputConfirmPassword" :placeholder="$t('user.passwordConfirmation')"/>
                <button class="btn-icon" type="button" name="button" data-test="btnShowConfirmPassword" @click="showConfirmNewPassword = !showConfirmNewPassword">
                  <IconEyePassword v-if="showConfirmNewPassword" />
                  <IconShowPassword v-else />
                </button>
                <span class="vee-error">{{ errorMessage }}</span>
              </vee-field>
              <label for="passwordConfirmation">{{ $t('user.passwordConfirmation') }}</label>
            </div>

          <div class="offcanvas-footer">
            <button type="submit" class="btn" :class="okButtonClass" :disabled="loading" data-test="btnConfirm">{{ okButton }}</button>
            <button type="button" class="btn" :class="cancelButtonClass" data-bs-dismiss="offcanvas" v-show="cancelButton" data-test="btnCancel" @click="doCancel">{{ cancelButton }}</button>
          </div>
        </form>
      </vee-form>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.offcanvas {
  // may open stacked on top of another already-open offcanvas (e.g. UserShowOffcanvas);
  // needs to sit above the raised backdrop set in styles/global/_offcanvas.scss (1047)
  // to stay interactive instead of being covered by it (position:fixed already set by Bootstrap).
  z-index:                               1048;

  .offcanvas-body {
    form {
      display:                          flex;
      flex-direction:                   column;
      gap:                              26px;
    }

    .vee-error {
      width:                            100% !important;
    }

    .strength-password {
      position:                       absolute;
      color:                          white;
      font-size:                      0.7em;
      text-align:                     center;
      height:                         12px;
      width:                          100%;
      top:                            -10px;
      &.danger {
        background:                   $phelcom-red;
      }
      &.warning {
        background:                   $yellow-phelcom;
      }
      &.success {
        background:                   $phelcom-green;
      }
    }

    .btn-icon {
      :deep(svg) {
        * {
          fill:                       var(--Brand-Primary);
        }
      }
    }
  }

  .offcanvas-footer {
    display:                            flex;
    flex-direction:                     column;
    gap:                                26px;

    .btn {
      font-size:                        var(--font-size-14);
      font-weight:                      700;
      line-height:                      normal;
      letter-spacing:                   4.2px;
      text-align:                       center;
      text-transform:                   uppercase;
      width:                            100%;
      padding:                          20px;
    }

    .btn-success {
      color:                            var(--Shades-W, #FFF);
    }
  }
}
</style>

<script>
import { useUserStore } from '@/store/user'

export default {
  name: 'UserChangePasswordOffcanvas',
  props: {
    offcanvas_id: { type: String, default: null },
  },
  data: () => ({
    loading: false,
    title: null,
    text: null,
    okButton: null,
    okButtonClass: null,
    cancelButton: null,
    cancelButtonClass: null,
    offcanvas_password: null,
    currentPassword: "",
    newPassword: "",
    newPasswordConfirmation: "",
    showCurrentPassword: false,
    showNewPassword: false,
    showConfirmNewPassword: false,
    strengthPasswordMessage: 'zxcvbnMessages.default',
    strengthInfo: null,
    password: "",
    confirmPassword: "",
    disabled: false,
    account: {
      firstName: "",
      lastName: "",
      email: "",
      password: "",
      pin: "",
    },
  }),
  mounted() {
    const userStore = useUserStore()
    this.session = userStore.sessionInfo

    if (this.session) {
      this.account.firstName = this.session.collaborator.firstName
      this.account.lastName = this.session.collaborator.lastName
      this.account.email = this.session.collaborator.email
    }

    if (this.external) {
      this.checkEmail()
    }

    this.$nextTick(() => {
      this.$refs.currentPassword.focus()
    })

    this.disableCopyPaste();
  },
  methods: {
    show(options = {}) {
      this.resetForm()

      if (options.title)
        this.title = options.title

      if (options.text)
        this.text = options.text

      if (options.okButton) {
        this.okButton = options.okButton
        this.okButtonClass = options.okButtonClass
      }

      if (options.cancelButton) {
        this.cancelButton = options.cancelButton
        this.cancelButtonClass = options.cancelButtonClass
      }

      const element = document.getElementById(this.offcanvas_id);
      this.offcanvas_password = bootstrap.Offcanvas.getOrCreateInstance(element);
      this.offcanvas_password.show();

      return new Promise((resolve, reject) => {
        this.resolvePromise = resolve
        this.rejectPromise = reject
      })
    },

    doConfirm() {
      this.closeOffcanvas()
      this.resolvePromise(true)
    },

    doCancel() {
      this.closeOffcanvas()
      this.resolvePromise(false)
    },

    resetForm() {
      this.currentPassword = ''
      this.newPassword = ''
      this.newPasswordConfirmation = ''
      this.strengthInfo = null
      this.strengthPasswordMessage = 'zxcvbnMessages.default'

      if (this.$refs.changePasswordForm)
        this.$refs.changePasswordForm.resetForm()
    },

    closeOffcanvas() {
      if (this.offcanvas_password)
        this.offcanvas_password.hide()

      // this.offcanvas_password = null
    },

    async checkPassword(password) {
      this.account.password = password

      let params = {
        account: this.account
      }

      let isValidPassword = await this.$utils.validatePassword(params.account)

      switch (isValidPassword.score) {
        case 0:
        case 1:
        case 2:
          this.strengthInfo = 'danger'
          break;
        case 3:
          this.strengthInfo = 'warning'
          break;
        case 4:
          this.strengthInfo = 'success'
          break;
      }

      if (isValidPassword.valid) {
        this.strengthPasswordMessage = 'zxcvbnMessages.default'
      } else {
        this.strengthPasswordMessage = isValidPassword.warning ? 'zxcvbnMessages.warnings.' + isValidPassword.warning :
                                                            'zxcvbnMessages.suggestions.' + isValidPassword.suggestion[0]
      }
    },
    async changePassword() {
      try {
        let params = {
          account: this.account,
        }
        let responseValidate = await this.$utils.validatePassword(params.account)

        if (responseValidate.valid == false){
          this.$message.popup(this, 'zxcvbnMessages.error', 'operationFail', 'error')
          return
        }

        this.loading = true
        NProgress.start()

        let result = await this.$userService.changePassword(this.newPassword, this.currentPassword)

        this.$message.popup(this, 'general.updatedSuccess', 'operationSuccess', 'success')

        this.$nextTick(() => {
          this.doConfirm();
          this.resetForm();
        });
      } catch(err) {
        const errorMessage = err.response?.data?.errorMessage
        const warning = err.response?.data?.warning

        const messageKeys = {
          invalidCurrentPassword: 'changePassword.invalidCurrentPassword',
          newPasswordSameAsOld: 'changePassword.newPasswordSameAsOld',
          passwordReused: 'changePassword.passwordReused',
          invalidPassword: warning ? `zxcvbnMessages.warnings.${warning}` : 'zxcvbnMessages.error',
        }

        this.$message.popup(this, messageKeys[errorMessage] || 'general.updatedError', 'operationFail', 'error')
      } finally {
        this.loading = false
        NProgress.done(true)
      }
    },
    async checkEmail() {
      await this.$helpers.requestHandling(async () => {
        const result = await this.$externalService.checkEmail()

        if (!result.isEmailValid) {
          this.disabled = true;
          this.alert = {
            message: this.$i18n.t('user.validEmail'),
            class: 'alert-warning'
          }
        }
      }, this);
    },
    disableCopyPaste() {
      let inputArray = [this.$refs.newPassword, this.$refs.passwordConfirmation]
      inputArray.forEach((element) => {
        element.addEventListener('contextmenu', (e) => e.preventDefault()); // Disable right-click menu
        element.addEventListener('cut', (e) => e.preventDefault()); // Disable cut
        element.addEventListener('copy', (e) => e.preventDefault()); // Disable copy
        element.addEventListener('paste', (e) => e.preventDefault()); // Disable paste
      })
    },
  }
}
</script>
